<template>
    <div class="widget-template-help">
        <div class="widget-template-help__image bg-light-bg">
            <div class="flex h-full w-full flex-center">
                <div class="relative p-2">
                    <div
                        v-for="(item, index) in itemsList"
                        :key="index"
                    >
                        <div
                            class="absolute z-50 cursor-help"
                            :style="getStyle(item)"
                        >
                            <div
                                class="ellipse-wrapper flex items-center justify-center"
                                @mouseover="onEllipseMouseOver(item.ItemNumber)"
                                @mouseleave="onEllipseMouseLeave()"
                            >
                                <div
                                    class="ellipse-outer flex items-center justify-center"
                                >
                                    <div class="ellipse-inner flex items-center justify-center">
                                        <span>{{ item.ItemNumber }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="isHelpImage"
                        class="w-full h-full"
                    >
                        <img
                            class="w-full h-auto object-contain"
                            alt="Preview"
                            :src="widgetHelpPicture"
                        >
                    </div>
                    <template v-else>
                        <component
                            :is="widgetHelpPicture"
                            class="w-64 h-48"
                        />
                    </template>
                </div>
            </div>
        </div>
        <div class="widget-template-help__sidebar">
            <div class="text-sm font-semibold mb-2 space-x-2">
                <span>{{ templateHelp.TemplateName }}</span>
                <RedesignBasePopover
                    trigger="hover"
                    :width="320"
                >
                    <template #reference>
                        <span class="text-primary inline-flex items-center justify-center">
                            <i class="vc-icon-help text-sm" />
                        </span>
                    </template>
                    <div
                        class="text-xs max-h-96 overflow-y-auto px-1 break-normal"
                    >
                        <div>
                            {{ templateHelp.Help?.WidgetDescription }}
                        </div>
                        <div style="font-size: 12px">
                            {{ `${$t('general.usage')}: ${templateHelp.Help?.WidgetUsage}` }}
                        </div>
                    </div>
                </RedesignBasePopover>
            </div>
            <div class="text-xs overflow-y-auto max-h-96">
                <div
                    v-for="(item, index) in itemsList"
                    :key="index"
                >
                    <div class="main-text-color py-2">
                        {{ item.ItemName }}
                    </div>
                    <div
                        class="flex gap-1 items-baseline break-normal p-1"
                        :class="{'bg-primary-100 rounded': item.ItemNumber === activeItemNumber}"
                    >
                        <b>{{ item.ItemNumber }}</b>.
                        <span class="text-xs main-text-color">{{ item.ItemDescription }}</span>
                    </div>
                </div>
            </div>
        </div>
        <portal to="widget-modal-actions">
            <div class="flex items-center gap-2 justify-end">
                <RedesignBaseButton
                    type="soft"
                    class="min-w-30"
                    @click="onCloseHelpInfo"
                >
                    {{ $t('general.back') }}
                </RedesignBaseButton>
            </div>
        </portal>
    </div>
</template>

<script setup lang="ts">
import { TemplateHelp, TemplateHelpItem } from '@/types/template-help'
import { computed, ref } from 'vue'
import RedesignBaseButton from '@/components/RedesignBaseComponents/Buttons/RedesignBaseButton.vue'
import RedesignBasePopover from '@/components/RedesignBaseComponents/RedesignBasePopover.vue'

// TODO: need to update images and items position logic

const ONE_REM_IN_PX = 16

const props = defineProps<{
    templateHelp: TemplateHelp
}>()
const emits = defineEmits([ 'close' ])

const activeItemNumber = ref(0)

const itemsList = computed(() => {
    return props.templateHelp.Help?.Items || []
})
const widgetHelpPicture = computed(() => {
    return props.templateHelp.Help?.WidgetHelpPicture
})
const isHelpImage = computed(() => {
    return widgetHelpPicture.value?.startsWith('http')
})

function onCloseHelpInfo () {
    emits('close')
}
function getStyle (item: TemplateHelpItem) {
    return {
        'margin-top': +item.ItemPositionY * ONE_REM_IN_PX + 'px',
        'margin-left': +item.ItemPositionX * ONE_REM_IN_PX + 'px',
    }
}
function onEllipseMouseOver (itemNumber: number) {
    activeItemNumber.value = itemNumber
}
function onEllipseMouseLeave () {
    activeItemNumber.value = 0
}
</script>

<style lang="scss">
    .widget-template-help {
        @apply w-full h-full min-h-40 grid grid-cols-3;
        &__sidebar {
            @apply px-3 col-span-1;
        }
        &__image {
            @apply p-3 col-span-2;
        }
        .ellipse-wrapper {
            @apply w-6 h-5 rounded border border-ui-lines bg-primary text-xs text-white;
        }
    }
</style>
